import { Button } from "@/components/ui/button";
import { Heart, BookOpen, Coins, Users } from "lucide-react";

const CinematicHero = () => {
  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-babyBlue-light/20 via-white to-sunrise-light/10">
      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-96 h-96 bg-legatia-green/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-[5%] w-64 h-64 bg-sunrise-light/20 rounded-full blur-3xl"></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          
          {/* Left Column - Content */}
          <div className="space-y-8 animate-fade-in">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-legatia-green/10 text-legatia-green px-4 py-2 rounded-full text-sm font-medium">
              🎉 Lancement bêta : Rejoignez les pionniers
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Faites fructifier l'amour que vous transmettez.
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              La première cagnotte intelligente qui transforme vos souvenirs en héritage émotionnel <strong>ET</strong> financier
            </p>

            {/* 3 Promises */}
            <div className="grid gap-4 py-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-legatia-green/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-legatia-green" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Racontez son histoire</h2>
                  <p className="text-gray-600 text-sm">Chaque photo, chaque moment devient un chapitre</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-legatia-green/10 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-legatia-green" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Construisez son avenir</h2>
                  <p className="text-gray-600 text-sm">Chaque contribution investie pour ses rêves futurs</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-legatia-green/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-legatia-green" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Impliquez toute la famille</h2>
                  <p className="text-gray-600 text-sm">Grands-parents, parrains, amis... tous participent</p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                className="bg-legatia-green hover:bg-legatia-green/90 text-white font-semibold text-lg px-8 py-6 rounded-full shadow-lg transform hover:scale-105 transition-all"
                onClick={() => window.location.href = "https://calendly.com/legatia/15min"}
              >
                <Heart className="w-5 h-5 mr-2" />
                Créer son histoire
              </Button>
              
              <Button 
                variant="outline"
                className="border-2 border-legatia-green text-legatia-green hover:bg-legatia-green/10 text-lg px-8 py-6 rounded-full transition-all"
                onClick={() => document.getElementById("calculator-section")?.scrollIntoView({ behavior: "smooth" })}
              >
                Voir un exemple concret
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-6 pt-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-legatia-green/20 border-2 border-white"></div>
                  <div className="w-8 h-8 rounded-full bg-sunrise-light/40 border-2 border-white"></div>
                  <div className="w-8 h-8 rounded-full bg-babyBlue-light/40 border-2 border-white"></div>
                </div>
                <span className="font-medium">+250 familles</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-legatia-green">★★★★★</span>
                <span className="font-medium">4.9/5</span>
              </div>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="relative animate-fade-in lg:animate-slide-in-right">
            {/* Shadow effect */}
            <div className="absolute inset-0 bg-legatia-green/10 rounded-3xl transform translate-x-4 translate-y-4" />
            
            {/* Main image container */}
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/assets/hero-newborn.jpg" 
                alt="Famille créant des souvenirs ensemble" 
                className="w-full h-full object-cover aspect-[4/5]"
                width="616"
                height="770"
                fetchPriority="high"
              />
              
              {/* Overlay card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-lg flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-legatia-green/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-legatia-green" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">1er anniversaire 🎂</p>
                  <p className="text-sm text-gray-600">300€ investis pour son futur</p>
                  <div className="mt-2 h-2 bg-legatia-green/20 rounded-full overflow-hidden">
                    <div className="h-full bg-legatia-green w-1/3 rounded-full" />
                  </div>
                </div>
              </div>
            </div>

            {/* Floating element */}
            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-sunrise-light/30 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CinematicHero;
